import { useEffect, useRef, useState } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { FINANCE_PRODUCTS_UK } from '@core/Constants/Filters/PriceFilter.constants';
import type { Filter, FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { useInitialMount } from '@core/Hooks/UseInitialMount';
import { ukLocale } from '@core/Utils/Intl/MappedLocales';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import Button from '@gds/Button/Button';
import { Pound } from '@gds/Icons/Paths/Pound';
import { Input } from '@gds/Input/Input';
import { SegmentedButtons } from '@gds/SegmentedButtons/SegmentedButtons';
import { SelectionControls } from '@gds/SelectionControls/SelectionControls';
import Slider from '@gds/Slider/Slider';
import { Stepper } from '@gds/Stepper/Stepper';
import { Typography } from '@gds/Typography/Typography';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { openQuickFilterAtom } from '@plp/Atoms/QuickFilterOpenAtom';
import { MultiSelect } from '@plp/Components/MultiSelect/MultiSelect';
import { getOptionsWithAggs } from '@plp/Utils/Filters/GetOptionsWithAggs';
import { type PriceTypeAtom, priceTypeAtom } from '@search-filters/Atom/PriceTypeAtom';
import { useMultiSelectFilters } from '@search-filters/Hooks/UseMultiSelectFilters';
import { usePriceRangeFilters } from '@search-filters/Hooks/UsePriceRangeFilters';
import { useUpdateFilters } from '@search-filters/Hooks/UseUpdateFilters';

import type { FilterControlBaseProps, PlpFiltersMessagesProps } from '../../FilterPanel.entity';
import { trackFilterEvent } from '../../Filters.tracking';
import ApplyButton from '../ApplyButton/ApplyButton';
import { PRICE_REDUCED_KEY, PRICE_TYPES } from './PriceFilter.constants';
import { transformFiltersForTracking } from './PriceFilter.utils';

import piStyles from '../PairedInputs/PairedInputs.module.css';
import styles from './PriceFilter.module.css';

type PriceFilterProps = FilterControlBaseProps;
const filterId: FilterKey = 'finance-product';

export const PriceFilter = ({
  strings,
  isPlpFiltersSeoEnabled,
  tenant,
  applyButton,
}: PriceFilterProps) => {
  const flags = useAtomValue(flagsAtom);
  const aggs = useAtomValue(aggregationsAtom);
  const { 'finance-product': financeProductsAggs } = useAtomValue(aggregationsAtom);
  const [priceType, setPriceType] = useAtom(priceTypeAtom);
  const setOpenQuickFilter = useSetAtom(openQuickFilterAtom);
  const filtersList = useAtomValue(filtersListAtom);
  const isInitialMount = useInitialMount();
  const hasUserTouchedType = useRef(false);
  const gteInputRef = useRef<HTMLInputElement>(null);
  const lteInputRef = useRef<HTMLInputElement>(null);
  const { updateFilters } = useUpdateFilters();

  const monthlyPriceRange = Object.keys(aggs['monthly-price']).map(str => Number(str));

  const ranges = {
    'monthly-price': monthlyPriceRange,
    price: Object.keys(aggs.price).map(str => Number(str)),
  };

  const MIN = ranges[priceType].length ? ranges[priceType][0].toString() : '0';
  const MAX = ranges[priceType].length
    ? ranges[priceType][ranges[priceType].length - 1].toString()
    : '0';

  const { handleChange, gteInputProps, lteInputProps, sliderProps, canReset, reset } =
    usePriceRangeFilters(priceType, ranges[priceType], [MIN, MAX], applyButton);

  const btnDataIntl = PRICE_TYPES.map(type => ({
    ...type,
    label: strings[type.id as keyof PlpFiltersMessagesProps],
    isDisabled: type.id === 'monthly-price' && monthlyPriceRange.length === 0,
  }));

  const reduced = getFilterFromArray(PRICE_REDUCED_KEY, filtersList)?.value;

  useEffect(
    () => {
      if (!isInitialMount && hasUserTouchedType.current) {
        handleChange([
          [`${priceType}__gte`, MIN as string],
          [`${priceType}__lte`, MAX as string],
        ]);
      }
    },
    applyButton ? [] : [priceType],
  );

  useEffect(() => {
    setLocalGteValue(gteInputProps.value);
    setLocalLteValue(lteInputProps.value);
  }, [gteInputProps.value, lteInputProps.value]);

  const [isLocalPriceReduced, setLocalReduced] = useState(reduced === 'true');
  const [pendingSelections, setPendingSelections] = useState<Record<string, boolean>>({});
  const [localGteValue, setLocalGteValue] = useState(gteInputProps.value);
  const [localLteValue, setLocalLteValue] = useState(lteInputProps.value);

  const handlePriceReducedChange = (eventTarget: EventTarget & HTMLInputElement) => {
    const isChecked = eventTarget.checked;
    setLocalReduced(isChecked);
    if (!applyButton) {
      trackFilterEvent(PRICE_REDUCED_KEY, isChecked ? 'true' : 'false', filtersList);
      updateFilters([
        {
          id: PRICE_REDUCED_KEY,
          value: isChecked ? 'true' : undefined,
        },
      ]);
    }
  };

  const financeProducts: Record<string, any> = FINANCE_PRODUCTS_UK;

  const { handleSelect, listWithSelected } = useMultiSelectFilters(
    filterId,
    getOptionsWithAggs(financeProducts, financeProductsAggs ?? {}),
    strings,
    isPlpFiltersSeoEnabled,
    tenant,
  );

  const handleApply = () => {
    const reducedFilter: Filter = {
      id: PRICE_REDUCED_KEY as FilterKey,
      value: isLocalPriceReduced ? 'true' : undefined,
    };

    let financeFilters: Filter[] = [];

    const selectedValues = Object.entries(pendingSelections)
      .filter(([_, isChecked]) => isChecked)
      .map(([id]) => id);

    financeFilters = [{ id: filterId as FilterKey, value: selectedValues }];

    const priceFilters: Filter[] = [
      {
        id: `${priceType}__gte` as FilterKey,
        value: localGteValue,
      },
      {
        id: `${priceType}__lte` as FilterKey,
        value: localLteValue,
      },
    ];
    const allFilters: Filter[] = [reducedFilter, ...financeFilters, ...priceFilters];

    const { filterName, filterValues } = transformFiltersForTracking(allFilters);

    trackFilterEvent(filterName, filterValues, filtersList);
    updateFilters(allFilters);
    setOpenQuickFilter(null);
  };

  const handleSelectionUpdate = (
    prevSelections: Record<string, boolean>,
    id: string,
    checked: boolean,
    listWithSelected: any[],
  ) => {
    const updatedSelections = { ...prevSelections };
    updatedSelections[id] = checked;

    listWithSelected.forEach(selectedItem => {
      if (!(selectedItem.id in updatedSelections)) {
        updatedSelections[selectedItem.id] = selectedItem.selected ?? false;
      }
    });
    return updatedSelections;
  };

  return (
    <section className={styles.priceFilter}>
      {!flags.promotionsFilter && (
        <SelectionControls
          checked={reduced === 'true'}
          className={styles.discountSwitch}
          label={strings.discountOnly}
          onChange={e => handlePriceReducedChange(e.target as EventTarget & HTMLInputElement)}
          type="switch"
        />
      )}
      <SegmentedButtons
        buttons={btnDataIntl}
        className={styles.priceTypeBtns}
        currentValue={priceType}
        onChange={val => {
          hasUserTouchedType.current = true;
          setPriceType(val as PriceTypeAtom);
          !applyButton && updateFilters([{ id: 'finance-product', value: [] }]);
        }}
      />
      {priceType === 'monthly-price' && tenant !== 'rac' && (
        <div className={styles.financeProduct}>
          <MultiSelect
            title={strings.financeType}
            groupName="finance-product"
            classes={{ label: styles.listItem }}
            items={listWithSelected}
            onSelect={(id, checked) => {
              if (applyButton) {
                setPendingSelections(prev => {
                  const updatedSelections = handleSelectionUpdate(
                    prev,
                    id,
                    checked,
                    listWithSelected,
                  );
                  return updatedSelections;
                });
              } else {
                handleSelect(id, checked);
              }
            }}
            applyButton={applyButton}
          />
        </div>
      )}
      <div className={piStyles.pairedInputs}>
        <Input
          {...(applyButton ? {} : gteInputProps)}
          ref={gteInputRef}
          dataTestId="price-filter-min-input"
          value={localGteValue}
          onChange={e => setLocalGteValue(e.target.value)}
          label={strings.min}
          leftIcon={<Pound />}
          leftIconClassName={styles.currencyIconWrapper}
          rightAddonsClassName={styles.stepperWrapper}
          rightAddons={
            <Stepper
              label="minimum price"
              focusTargetRef={gteInputRef}
              onIncrement={() => setLocalGteValue(String(Number(localGteValue || 0) + 1))}
              onDecrement={() => setLocalGteValue(String(Number(localGteValue || 0) - 1))}
            />
          }
          className={styles.priceInput}
          wrapperClassName={styles.priceInputControl}
          type="number"
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
        <Input
          {...(applyButton ? {} : lteInputProps)}
          ref={lteInputRef}
          dataTestId="price-filter-max-input"
          value={localLteValue}
          onChange={e => setLocalLteValue(e.target.value)}
          label={strings.max}
          leftIcon={<Pound />}
          leftIconClassName={styles.currencyIconWrapper}
          rightAddonsClassName={styles.stepperWrapper}
          rightAddons={
            <Stepper
              label="maximum price"
              focusTargetRef={lteInputRef}
              onIncrement={() => setLocalLteValue(String(Number(localLteValue || 0) + 1))}
              onDecrement={() => setLocalLteValue(String(Number(localLteValue || 0) - 1))}
            />
          }
          className={styles.priceInput}
          wrapperClassName={styles.priceInputControl}
          type="number"
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
      </div>
      <div className={styles.auxInfo}>
        <Typography Component="span" className={styles.resultCount} variant="caption2">
          ({aggs.totalCount.toLocaleString(ukLocale)})
        </Typography>
        <Button
          color="secondary"
          disabled={!canReset()}
          onClick={() => reset()}
          size="small"
          variant="link"
        >
          {strings.reset}
        </Button>
      </div>
      <Slider {...sliderProps} className={styles.priceRangeSlider} />
      {applyButton && <ApplyButton handleApply={handleApply} ctaText={strings.apply} />}
    </section>
  );
};
